import React, { useState } from 'react';
import {
    Shield,
    Check,
    X,
    AlertTriangle,
    Clock,
    Filter,
    Loader2,
    ChevronDown,
    RefreshCw,
    Eye,
    MessageSquare,
    Edit
} from 'lucide-react';
import { useEscalations, type Escalation } from '../hooks/useEscalations';
import { DecisionBadge } from '../components/PendingApprovalsQueue';
import DecisionPackageView from '../components/DecisionPackageView';

// ============================================================================
// TYPES
// ============================================================================

interface DecisionPackage {
    version: string;
    generatedAt: string;
    escalationId: string;
    decisionStatement: string;
    contextSummary: {
        currentSituation: string;
        constraints: string[];
        stakeholders: string[];
        timeSensitivity: string;
        missingInformation: string[];
    };
    subDecisions: Array<{
        question: string;
        explanation: string;
        considerations: string[];
    }>;
    options: Array<{
        id: string;
        description: string;
        pros: string[];
        cons: string[];
        risks: string[];
        respectsScope: boolean;
        respectsPolicy: boolean;
    }>;
    uncertainty: {
        confidenceScore: number;
        assumptions: string[];
        knownUnknowns: string[];
        dataGaps: string[];
        confidenceStatement: string;
    };
    actionClass: 'A' | 'B' | 'C';
    actionClassReason: string;
    humanActions: string[];
}

// ============================================================================
// ESCALATION DETAIL MODAL (Enhanced with DecisionPackage)
// ============================================================================

interface EscalationDetailModalProps {
    escalation: Escalation | null;
    onClose: () => void;
    onApprove: (id: string, feedback: string) => Promise<boolean>;
    onReject: (id: string, feedback: string) => Promise<boolean>;
}

const EscalationDetailModal: React.FC<EscalationDetailModalProps> = ({
    escalation,
    onClose,
    onApprove,
    onReject,
}) => {
    const [feedback, setFeedback] = useState('');
    const [processing, setProcessing] = useState(false);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);

    if (!escalation) return null;

    // Try to extract DecisionPackage from verificationResult
    let decisionPackage: DecisionPackage | null = null;
    let rawDetails: Record<string, unknown> = {};

    try {
        // The backend now stores decisionPackage inside verificationResult
        const verificationData = JSON.parse(escalation.decisionContent);
        if (verificationData.decisionPackage) {
            decisionPackage = verificationData.decisionPackage;
        } else {
            rawDetails = verificationData;
        }
    } catch {
        // Fallback to raw display
        rawDetails = { raw: escalation.decisionContent };
    }

    const handleApprove = async () => {
        if (decisionPackage && !selectedOption) {
            // Require option selection when DecisionPackage is available
            alert('Please select an option before approving');
            return;
        }

        setProcessing(true);
        const feedbackWithOption = selectedOption
            ? `Approved option: ${selectedOption}. ${feedback || ''}`
            : feedback || 'Approved';
        const success = await onApprove(escalation.id, feedbackWithOption);
        if (success) onClose();
        setProcessing(false);
    };

    const handleReject = async () => {
        setProcessing(true);
        const success = await onReject(escalation.id, feedback || 'Rejected');
        if (success) onClose();
        setProcessing(false);
    };

    const getRiskColor = (level: string) => {
        switch (level) {
            case 'high': return 'text-red-500 bg-red-500/10 border-red-500/20';
            case 'medium': return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20';
            default: return 'text-green-500 bg-green-500/10 border-green-500/20';
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" role="presentation">
            <div className="bg-hig-bg-secondary rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-hig-border" role="dialog" aria-modal="true" aria-labelledby="escalation-modal-title">
                {/* Header */}
                <div className="sticky top-0 bg-hig-bg-secondary border-b border-hig-border p-6 z-10">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Shield className="text-hig-accent" size={20} />
                                <h2 id="escalation-modal-title" className="text-xl font-semibold text-hig-text-primary">
                                    Decision Review Required
                                </h2>
                            </div>
                            <p className="text-sm text-hig-text-secondary">
                                {escalation.replicaName} • {escalation.decisionType}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-hig-text-tertiary hover:text-hig-text-primary p-2 rounded-lg hover:bg-hig-bg-tertiary"
                            aria-label="Close detail modal"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Status badges */}
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium uppercase border ${getRiskColor(escalation.riskLevel)}`}>
                            {escalation.riskLevel} risk
                        </span>
                        <DecisionBadge type="blocking" />
                        <div className="flex items-center gap-1 text-sm text-hig-text-tertiary">
                            <Clock size={14} />
                            <span>{escalation.proposedAt}</span>
                        </div>
                    </div>

                    {/* Warning box */}
                    <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
                        <div className="flex gap-3">
                            <AlertTriangle className="text-orange-500 flex-shrink-0" size={20} />
                            <div>
                                <p className="font-medium text-orange-500">FAIL-CLOSED: Action Blocked</p>
                                <p className="text-sm text-hig-text-secondary mt-1">
                                    This Replica is blocked from proceeding until you approve or reject this decision.
                                    {escalation.expiresAt && (
                                        <span className="block mt-1">
                                            Auto-expires: {new Date(escalation.expiresAt).toLocaleString()}
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Decision Package View (HRM-Style) OR Raw Details */}
                    {decisionPackage ? (
                        <DecisionPackageView
                            pkg={decisionPackage}
                            selectedOption={selectedOption}
                            onSelectOption={setSelectedOption}
                        />
                    ) : (
                        <div className="bg-hig-bg-tertiary rounded-lg p-4">
                            <h3 className="font-medium text-hig-text-primary mb-3">Decision Details</h3>
                            <dl className="space-y-2 text-sm">
                                {Object.entries(rawDetails).map(([key, value]) => (
                                    <div key={key} className="flex gap-2">
                                        <dt className="text-hig-text-tertiary capitalize min-w-[100px]">
                                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                                        </dt>
                                        <dd className="text-hig-text-secondary">
                                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    )}

                    {/* Feedback input */}
                    <div>
                        <label htmlFor="escalation-feedback" className="block text-sm font-medium text-hig-text-primary mb-2">
                            <MessageSquare size={14} className="inline mr-1" />
                            Decision Feedback (optional)
                        </label>
                        <textarea
                            id="escalation-feedback"
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            placeholder="Add a note explaining your decision..."
                            className="w-full px-4 py-3 bg-hig-bg-tertiary border border-hig-border rounded-lg text-hig-text-primary placeholder:text-hig-text-tertiary focus:outline-none focus:ring-2 focus:ring-hig-accent resize-none"
                            rows={3}
                        />
                    </div>
                </div>

                {/* Footer actions */}
                <div className="sticky bottom-0 bg-hig-bg-secondary border-t border-hig-border p-6">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-hig-text-secondary hover:text-hig-text-primary"
                        >
                            Cancel
                        </button>
                        <div className="flex gap-3">
                            <button
                                onClick={handleReject}
                                disabled={processing}
                                className="px-6 py-2 text-sm font-medium text-hig-error hover:bg-hig-error/10 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {processing ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
                                Reject
                            </button>
                            <button
                                onClick={handleApprove}
                                disabled={processing || (decisionPackage && !selectedOption)}
                                className="px-6 py-2 text-sm font-medium bg-hig-success text-white hover:bg-hig-success/90 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {processing ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                                {decisionPackage && selectedOption ? 'Approve Selected' : 'Approve'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// ESCALATION QUEUE PAGE
// ============================================================================

type StatusFilter = 'all' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';

const EscalationQueuePage: React.FC = () => {
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('PENDING');
    const [selectedEscalation, setSelectedEscalation] = useState<Escalation | null>(null);

    const { escalations, loading, error, refetch, approve, reject } = useEscalations(
        statusFilter === 'all' ? {} : { status: statusFilter as Escalation['status'] }
    );

    const getStatusBadge = (status: string) => {
        const config: Record<string, { bg: string; text: string }> = {
            PENDING: { bg: 'bg-orange-500/10', text: 'text-orange-500' },
            APPROVED: { bg: 'bg-green-500/10', text: 'text-green-500' },
            REJECTED: { bg: 'bg-red-500/10', text: 'text-red-500' },
            EXPIRED: { bg: 'bg-gray-500/10', text: 'text-gray-500' },
        };
        const { bg, text } = config[status] || config.PENDING;
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${bg} ${text}`}>
                {status}
            </span>
        );
    };

    const getRiskBadge = (level: 'high' | 'medium' | 'low') => {
        const config = {
            high: 'bg-red-500/10 text-red-500 border-red-500/20',
            medium: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
            low: 'bg-green-500/10 text-green-500 border-green-500/20',
        };
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase border ${config[level]}`}>
                {level}
            </span>
        );
    };

    const pendingCount = escalations.filter(e => e.status === 'PENDING').length;

    return (
        <div className="min-h-screen bg-hig-bg-primary py-8 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <Shield className="text-hig-accent" size={28} />
                            <h1 className="text-2xl font-bold text-hig-text-primary">
                                Decision Guardian™ Queue
                            </h1>
                            {pendingCount > 0 && (
                                <span className="px-3 py-1 bg-hig-accent text-white text-sm font-bold rounded-full">
                                    {pendingCount} pending
                                </span>
                            )}
                        </div>
                        <p className="text-hig-text-secondary">
                            Review and resolve escalated decisions requiring human approval
                        </p>
                    </div>
                    <button
                        onClick={refetch}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-hig-bg-secondary border border-hig-border rounded-lg text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors disabled:opacity-50"
                    >
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                        <Filter size={16} className="text-hig-text-tertiary" />
                        <span className="text-sm text-hig-text-secondary">Status:</span>
                    </div>
                    <div className="flex gap-2">
                        {(['PENDING', 'APPROVED', 'REJECTED', 'EXPIRED', 'all'] as StatusFilter[]).map((status) => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${statusFilter === status
                                    ? 'bg-hig-accent text-white'
                                    : 'bg-hig-bg-secondary text-hig-text-secondary hover:text-hig-text-primary'
                                    }`}
                            >
                                {status === 'all' ? 'All' : status}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Loading */}
                {loading && (
                    <div className="text-center py-16">
                        <Loader2 size={32} className="animate-spin text-hig-accent mx-auto mb-4" />
                        <p className="text-hig-text-secondary">Loading escalations...</p>
                    </div>
                )}

                {/* Error */}
                {error && !loading && (
                    <div className="text-center py-16">
                        <AlertTriangle size={32} className="text-hig-error mx-auto mb-4" />
                        <p className="text-hig-text-primary font-medium mb-2">Failed to load escalations</p>
                        <p className="text-hig-text-secondary text-sm">{error}</p>
                        <button
                            onClick={refetch}
                            className="mt-4 px-4 py-2 bg-hig-accent text-white rounded-lg hover:bg-hig-accent/90"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Empty */}
                {!loading && !error && escalations.length === 0 && (
                    <div className="text-center py-16 bg-hig-bg-secondary rounded-xl border border-hig-border">
                        <div className="w-16 h-16 rounded-full bg-hig-success/10 flex items-center justify-center mx-auto mb-4">
                            <Check size={32} className="text-hig-success" />
                        </div>
                        <p className="text-hig-text-primary font-medium mb-2">No escalations found</p>
                        <p className="text-hig-text-secondary text-sm">
                            {statusFilter === 'PENDING'
                                ? 'All decisions have been reviewed'
                                : `No ${statusFilter.toLowerCase()} escalations`}
                        </p>
                    </div>
                )}

                {/* Escalation list */}
                {!loading && !error && escalations.length > 0 && (
                    <div className="space-y-4">
                        {escalations.map((escalation) => (
                            <div
                                key={escalation.id}
                                className="bg-hig-bg-secondary rounded-xl border border-hig-border p-6 hover:border-hig-accent/30 transition-colors"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="font-medium text-hig-text-primary">
                                                {escalation.replicaName}
                                            </span>
                                            {getStatusBadge(escalation.status)}
                                            {getRiskBadge(escalation.riskLevel)}
                                            {escalation.isBlocking && <DecisionBadge type="blocking" />}
                                        </div>
                                        <p className="text-sm text-hig-text-secondary mb-3">
                                            {escalation.decisionType}
                                        </p>
                                        <div className="flex items-center gap-4 text-xs text-hig-text-tertiary">
                                            <div className="flex items-center gap-1">
                                                <Clock size={12} />
                                                <span>Created {escalation.proposedAt}</span>
                                            </div>
                                            {escalation.expiresAt && escalation.status === 'PENDING' && (
                                                <div className="flex items-center gap-1 text-orange-500">
                                                    <AlertTriangle size={12} />
                                                    <span>Expires {new Date(escalation.expiresAt).toLocaleString()}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setSelectedEscalation(escalation)}
                                            className="flex items-center gap-2 px-4 py-2 bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary rounded-lg transition-colors"
                                        >
                                            <Eye size={16} />
                                            View Details
                                        </button>
                                        {escalation.status === 'PENDING' && (
                                            <>
                                                <button
                                                    onClick={() => reject(escalation.id, 'Quick reject')}
                                                    className="p-2 text-hig-error hover:bg-hig-error/10 rounded-lg transition-colors"
                                                    title="Reject"
                                                    aria-label="Quick reject"
                                                >
                                                    <X size={20} />
                                                </button>
                                                <button
                                                    onClick={() => approve(escalation.id, 'Quick approve')}
                                                    className="p-2 text-hig-success hover:bg-hig-success/10 rounded-lg transition-colors"
                                                    title="Approve"
                                                    aria-label="Quick approve"
                                                >
                                                    <Check size={20} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Detail Modal */}
                <EscalationDetailModal
                    escalation={selectedEscalation}
                    onClose={() => setSelectedEscalation(null)}
                    onApprove={approve}
                    onReject={reject}
                />
            </div>
        </div>
    );
};

export default EscalationQueuePage;
